import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PushNotificationSetup from './components/PushNotificationSetup';
import { AnalyticsRouteWatcher } from './components/AnalyticsRouteWatcher';
import { AdminRoute, CreateCondoRoute, DirectiveRoute, SecurityRoute, SuperAdminRoute } from './components/RoleRoute';
import { ProtectedRoute } from './components/ProtectedRoute';
import LandingPage from './pages/LandingPage';
import { RootAuthGate } from './components/RootAuthGate';
import LoginPage from './pages/LoginPage';
import Register from './pages/Register';
import CompletarRegistro from './pages/CompletarRegistro';
import CreateCondo from './pages/CreateCondo';
import UserManagement from './pages/UserManagement';
import PricingConfigPage from './pages/PricingConfigPage';
import BulkOnboardingPage from './pages/BulkOnboardingPage';
import EntitlementsMatrixPage from './pages/EntitlementsMatrixPage';
import BillingPortal from "./pages/BillingPortal";
import { AppShell } from './layouts/AppShell';
import { LensDashboard } from './pages/LensDashboard';
import Dashboard from './Dashboard';
import SecurityDashboard from './pages/SecurityDashboard';
import Visitors from './pages/Visitors';
import SectionManagement from './pages/SectionManagement';
import FinancialReports from './pages/FinancialReports';
import Morosos from './pages/Morosos';
import Expenses from './pages/Expenses';
import Voting from './pages/Voting';
import Calendar from './pages/Calendar';
import Announcements from './pages/Announcements';
import AnalyticsPage from './pages/AnalyticsPage';
import Amenities from './pages/Amenities';
// Family/Home modules
import Tasks from './pages/Tasks';
import ShoppingList from './pages/ShoppingList';
import PetCare from './pages/PetCare';
import VehicleMaintenance from './pages/VehicleMaintenance';
import KidsHomework from './pages/KidsHomework';
import HomeSecurity from './pages/HomeSecurity';
import HouseholdBudget from './pages/HouseholdBudget';
// HOME+ API-backed pages
import HomeTasks from './pages/HomeTasks';
import HomeShopping from './pages/HomeShopping';
import HomePets from './pages/HomePets';
import HomeVehicles from './pages/HomeVehicles';
import HomeBudget from './pages/HomeBudget';
// Maintenance
import Maintenance from './pages/Maintenance';
// New Priority Modules
import WorkOrders from './pages/WorkOrders';
import DailyDigest from './pages/DailyDigest';
import PackageTracker from './pages/PackageTracker';
import RouteOptimizer from './pages/RouteOptimizer';
import MobilePOS from './pages/MobilePOS';
// Missing modules
import VerifyQR from './pages/VerifyQR';
import QrAuditPage from './pages/QrAuditPage';
import SupplierProfile from './pages/SupplierProfile';
import NotificationsCenter from './pages/NotificationsCenter';
import SupplierDirectory from './pages/SupplierDirectory';
import SupplierRegistration from './pages/SupplierRegistration';
import SupplierDashboard from './pages/SupplierDashboard';
import SuperAdminCondoRequests from './pages/SuperAdminCondoRequests';
import SuperAdminUsage from './pages/SuperAdminUsage';
import SupplierLeads from './pages/SupplierLeads';
// Rental Property Modules
import ReservationManager from './pages/ReservationManager';
import TurnoverCoordination from './pages/TurnoverCoordination';
import GuestCommunication from './pages/GuestCommunication';
import SmartAccess from './pages/SmartAccess';
import RentalAnalytics from './pages/RentalAnalytics';
import DigitalGuidebook from './pages/DigitalGuidebook';
import PropertyLinkSettings from './pages/PropertyLinkSettings';
import PropertyPublicPage from './pages/PropertyPublicPage';
// Staff Management Modules
import StaffDirectory from './pages/StaffDirectory';
import StaffSchedule from './pages/StaffSchedule';
import StaffPayroll from './pages/StaffPayroll';
// Admin
import SuperAdmin from './pages/SuperAdmin';
import DirectiveUserDashboard from './pages/DirectiveUserDashboard';
import MonthlyClosure from './pages/MonthlyClosure';
import NotificationsPage from './components/Notifications';
// Mi ResidenciApp 2018 Modules
import QuejasYSugerencias from './pages/QuejasYSugerencias';
import NumerosEmergencia from './pages/NumerosEmergencia';
import Reglamentos from './pages/Reglamentos';
import CajaChica from './pages/CajaChica';
import CasetasVigilancia from './pages/CasetasVigilancia';
import EmpresasSeguridad from './pages/EmpresasSeguridad';
import PersonalSeguridad from './pages/PersonalSeguridad';
import ReporteVisitas from './pages/ReporteVisitas';
import InformacionCondominio from './pages/InformacionCondominio';
import EdicionPerfil from './pages/EdicionPerfil';
import LlaveDigital from './pages/LlaveDigital';
import MyQR from './pages/MyQR';
import PanicButton from './pages/PanicButton';
import TravelNotices from './pages/TravelNotices';
import QrScannerPage from './pages/QrScannerPage';
import VotacionesAvanzadas from './pages/VotacionesAvanzadas';
import Categorias from './pages/Categorias';
import ReservaPago from './pages/ReservaPago';
import OpenpayConfig from './pages/OpenpayConfig';
import ModuleConfigPage from './pages/ModuleConfigPage';
// P1-B (2026-09-08, spec ADDENDUM): Condo Admin Panel — identity + plan-bounded module toggles.
import CondoAdminPanel from './pages/CondoAdminPanel';
import PermissionManager from './pages/PermissionManager';
import Employees from './pages/Employees';
import InviteMember from './pages/InviteMember';
// Public pages
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AcceptInvite from './pages/AcceptInvite';
// Phase 2 visitor link invites (2026-09-12): public landing at /invite/:token.
import InviteLanding from './pages/InviteLanding';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import Support from './pages/Support';
import { PushSetup } from './pages/PushSetup';
import Pricing from './pages/Pricing';
import { PlanLimitModal } from './components/PlanLimitModal';
import { WalkInModal } from './components/WalkInModal';
import SecurityWalkIns from './pages/SecurityWalkIns';
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import Pagos from './pages/Pagos';
import Projects from './pages/Projects';
import MensajesPage from './pages/MensajesPage';
// F18 roster UI (packet 2026-09-24): condo-wide member roster for residents
// (limited fields user_id/name/role/unit_id/active) + self-serve block/unblock.
import RosterVecinos from './pages/RosterVecinos';
import NotFound from './pages/NotFound';

function ProjectsRoute() {
  const { user } = useAuth();
  const condoId = user?.condoId;
  if (!condoId) return <div className="p-6 text-gray-600">Selecciona un condominio para ver los proyectos.</div>;
  return <Projects condoId={condoId} />;
}


function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">Loading...</div>;
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
      {/* Web-push infra (2026-09-17): one-time post-login push permission prompt */}
      <PushNotificationSetup />
      {/* Product analytics (launch-readiness item 2): page_view per SPA route */}
      <AnalyticsRouteWatcher />
        <Routes>
          {/* Public Routes */}
          {/* UX fix (Jaime 2026-09-18): PWA start_url "/" must be auth-aware —
              logged-in users land in the app, visitors still get the marketing
              landing. Manifest start_url intentionally unchanged. */}
          <Route path="/" element={<RootAuthGate />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verificar-correo" element={<VerifyEmailPage />} />
          {/* P1 launch-blocker fix (2026-10-09): QA hit /verify-email directly and
              got a 404. Same landing, Spanish route stays canonical. */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/completar-whatsapp" element={<CompletarRegistro />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          {/* Phase 2 (2026-09-12): public visitor invite landing — OUTSIDE all
              auth guards (PrivateRoute/RoleRoute). Access never waits behind
              registration; the rotating pass renders inline after claim. */}
          <Route path="/invite/:token" element={<InviteLanding />} />
          {/* /privacidad (2026-09-16): full Spanish privacy policy draft.
              /privacy kept as alias so existing links (LoginPage, InviteLanding) keep working. */}
          <Route path="/privacidad" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          {/* /terminos (2026-09-15): canonical Spanish T&C path used by the
              invite-claim consent checkbox (alias of /terms). */}
          <Route path="/terminos" element={<Terms />} />
          {/* Public, token-bound email confirmation for claim-created
              visitor accounts (the emailed link target — no session yet). */}
          <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="/support" element={<Support />} />
          <Route path="/push-setup" element={<PrivateRoute><PushSetup /></PrivateRoute>} />

          {/* Pricing & Billing Routes */}
          <Route path="/pricing" element={<PrivateRoute><Pricing /></PrivateRoute>} />
          <Route path="/billing/checkout" element={<PrivateRoute><Checkout /></PrivateRoute>} />
          <Route path="/billing/success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />

          {/* Protected Routes - Condo Modules */}
          <Route path="/building/*" element={<PrivateRoute><AppShell><LensDashboard /></AppShell></PrivateRoute>} />
          <Route path="/home/*" element={<PrivateRoute><AppShell><LensDashboard /></AppShell></PrivateRoute>} />
          <Route path="/services/*" element={<PrivateRoute><AppShell><LensDashboard /></AppShell></PrivateRoute>} />
          {/* Legacy dashboards retired (2026-10-12 legacy-route cleanup): /legacy-dashboard and
              /dashboard-custom removed outright (zero references); /dashboard redirects into the home lens. */}
          <Route path="/dashboard" element={<PrivateRoute><Navigate to="/home/dashboard" replace /></PrivateRoute>} />
          <Route path="/security-dashboard" element={<SecurityRoute><SecurityDashboard /></SecurityRoute>} />
          <Route path="/visitors" element={<PrivateRoute><Navigate to="/building/visitors" replace /></PrivateRoute>} />
          <Route path="/sections" element={<PrivateRoute><SectionManagement /></PrivateRoute>} />
          <Route path="/financial-reports" element={<PrivateRoute><ProtectedRoute moduleId="finanzas"><FinancialReports /></ProtectedRoute></PrivateRoute>} />
          {/* 2026-10-01 UI smoke fix: /finances (stale deep link) and /finanzas alias -> real finance route. */}
          <Route path="/finances" element={<PrivateRoute><Navigate to="/financial-reports" replace /></PrivateRoute>} />
          <Route path="/finanzas" element={<PrivateRoute><Navigate to="/financial-reports" replace /></PrivateRoute>} />
          <Route path="/pagos" element={<PrivateRoute><Pagos /></PrivateRoute>} />
          <Route path="/payments" element={<PrivateRoute><Pagos /></PrivateRoute>} />
          <Route path="/projects" element={<PrivateRoute><ProjectsRoute /></PrivateRoute>} />
          {/* B2-3 (2026-10-01): /quotes 404 fix - the Cotizaciones module (F33-F36) is
               implemented by Projects.tsx (Projects & Quotes); wire the module
               route to the same component instead of falling through to NotFound. */}
          <Route path="/quotes" element={<PrivateRoute><ProjectsRoute /></PrivateRoute>} />
          <Route path="/mensajes" element={<PrivateRoute><MensajesPage /></PrivateRoute>} />
          <Route path="/messages" element={<PrivateRoute><Navigate to="/mensajes" replace /></PrivateRoute>} />
          {/* F18 roster (packet 2026-09-24): resident-facing condo member directory. */}
          <Route path="/vecinos" element={<PrivateRoute><RosterVecinos /></PrivateRoute>} />
          <Route path="/morosos" element={<PrivateRoute><ProtectedRoute moduleId="morosos"><Morosos /></ProtectedRoute></PrivateRoute>} />
          <Route path="/expenses" element={<PrivateRoute><ProtectedRoute moduleId="gastos"><Expenses /></ProtectedRoute></PrivateRoute>} />
          <Route path="/voting" element={<PrivateRoute><Navigate to="/building/voting" replace /></PrivateRoute>} />
          <Route path="/calendar" element={<PrivateRoute><ProtectedRoute moduleId="calendario"><Calendar /></ProtectedRoute></PrivateRoute>} />
          <Route path="/announcements" element={<PrivateRoute><ProtectedRoute moduleId="anuncios" allowWithoutCondo><Announcements /></ProtectedRoute></PrivateRoute>} />
          <Route path="/analytics" element={<PrivateRoute><ProtectedRoute moduleId="analytics"><AnalyticsPage /></ProtectedRoute></PrivateRoute>} />
          {/* Canonical condo-scoped analytics URL (also used by deep links). */}
          <Route path="/condos/:condoId/analytics" element={<PrivateRoute><ProtectedRoute moduleId="analytics"><AnalyticsPage /></ProtectedRoute></PrivateRoute>} />
          <Route path="/amenities" element={<PrivateRoute><ProtectedRoute moduleId="amenidades"><Amenities /></ProtectedRoute></PrivateRoute>} />

          {/* Protected Routes - Family/Home Modules (PrivateRoute so admins can access) */}
          <Route path="/tasks" element={<PrivateRoute><Navigate to="/home/tasks" replace /></PrivateRoute>} />
          <Route path="/billing" element={<PrivateRoute><BillingPortal /></PrivateRoute>} />
          <Route path="/shopping" element={<PrivateRoute><Navigate to="/home/shopping" replace /></PrivateRoute>} />
          <Route path="/pets" element={<PrivateRoute><Navigate to="/home/pets" replace /></PrivateRoute>} />
          <Route path="/vehicles" element={<PrivateRoute><Navigate to="/home/vehicles" replace /></PrivateRoute>} />
          <Route path="/homework" element={<PrivateRoute><KidsHomework /></PrivateRoute>} />
          <Route path="/security" element={<PrivateRoute><HomeSecurity /></PrivateRoute>} />
          <Route path="/budget" element={<PrivateRoute><Navigate to="/home/budget" replace /></PrivateRoute>} />
          <Route path="/maintenance" element={<PrivateRoute><Navigate to="/building/maintenance" replace /></PrivateRoute>} />
          {/* Legacy mock-page fallbacks (not routed, kept for reference) */}

          {/* Protected Routes - Supplier Modules */}
          <Route path="/suppliers" element={<PrivateRoute><SupplierDirectory /></PrivateRoute>} />
          <Route path="/become-supplier" element={<PrivateRoute><SupplierRegistration /></PrivateRoute>} />
          <Route path="/supplier-dashboard" element={<PrivateRoute><SupplierDashboard /></PrivateRoute>} />

          {/* New Priority Modules */}
          <Route path="/work-orders" element={<PrivateRoute><Navigate to="/building/work-orders" replace /></PrivateRoute>} />
          <Route path="/daily-digest" element={<PrivateRoute><DailyDigest /></PrivateRoute>} />
          <Route path="/packages" element={<PrivateRoute><PackageTracker /></PrivateRoute>} />
          <Route path="/route-optimizer" element={<PrivateRoute><RouteOptimizer /></PrivateRoute>} />
          <Route path="/mobile-pos" element={<PrivateRoute><MobilePOS /></PrivateRoute>} />

          {/* Rental Property Modules */}
          <Route path="/reservations" element={<PrivateRoute><Navigate to="/building/reservations" replace /></PrivateRoute>} />
          <Route path="/turnover" element={<PrivateRoute><Navigate to="/home/turnover" replace /></PrivateRoute>} />
          <Route path="/guest-comm" element={<PrivateRoute><GuestCommunication /></PrivateRoute>} />
          <Route path="/smart-access" element={<PrivateRoute><SmartAccess /></PrivateRoute>} />
          <Route path="/rental-analytics" element={<PrivateRoute><RentalAnalytics /></PrivateRoute>} />
          <Route path="/guidebook" element={<PrivateRoute><DigitalGuidebook /></PrivateRoute>} />
          <Route path="/property-link" element={<PrivateRoute><PropertyLinkSettings /></PrivateRoute>} />
          <Route path="/rental/:propertyId" element={<PropertyPublicPage />} />

          {/* Staff Management Modules */}
          <Route path="/staff-directory" element={<PrivateRoute><Navigate to="/building/staff-directory" replace /></PrivateRoute>} />
          <Route path="/staff-schedule" element={<PrivateRoute><StaffSchedule /></PrivateRoute>} />
          <Route path="/staff-payroll" element={<PrivateRoute><Navigate to="/home/staff-payroll" replace /></PrivateRoute>} />

          {/* Admin & Directive - Role Protected */}
          <Route path="/admin" element={<AdminRoute><SuperAdmin /></AdminRoute>} />
          <Route path="/super-admin" element={<AdminRoute><SuperAdmin /></AdminRoute>} />
          <Route path="/superadmin/analytics" element={<SuperAdminRoute><AnalyticsPage /></SuperAdminRoute>} />
          <Route path="/superadmin/usage" element={<SuperAdminRoute><SuperAdminUsage /></SuperAdminRoute>} />
          <Route path="/superadmin/condo-requests" element={<SuperAdminRoute><SuperAdminCondoRequests /></SuperAdminRoute>} />
          <Route path="/mis-leads" element={<PrivateRoute><SupplierLeads /></PrivateRoute>} />
          <Route path="/directive" element={<DirectiveRoute><DirectiveUserDashboard /></DirectiveRoute>} />
          <Route path="/directivo" element={<Navigate to="/directive" replace />} />
          <Route path="/monthly-closure" element={<AdminRoute><MonthlyClosure /></AdminRoute>} />
          {/* (2026-09-03, Jaime spec) Condo-wide Usuarios directory: condo admin lens only. */}
          {/* Vecinos directory route REMOVED per Jaime ruling (2026-09-08); VecinosPage.tsx kept on disk, reversible. */}
          <Route path="/building/staff-directory" element={<AdminRoute><AppShell><LensDashboard /></AppShell></AdminRoute>} />
          {/* (2026-09-09, Jaime) Condo admin 'Empleados' tab: condo-level
              employee management (Employees.tsx, unitId optional). LensDashboard
              already maps case '/building/employees' -> <Employees />. */}
          <Route path="/building/employees" element={<AdminRoute><AppShell><LensDashboard /></AppShell></AdminRoute>} />
          {/* G1 (2026-10-01, Jaime-approved): member invite UI (propietario/residente/
              familiar/inquilino) at /building/invites. LensDashboard maps the case to
              InviteMember.tsx; the AdminRoute wrapper mirrors /building/employees. */}
          <Route path="/building/invites" element={<AdminRoute><AppShell><LensDashboard /></AppShell></AdminRoute>} />
          <Route path="/building/join-requests" element={<AdminRoute><AppShell><LensDashboard /></AppShell></AdminRoute>} />
          <Route path="/notifications" element={<PrivateRoute><NotificationsCenter /></PrivateRoute>} />
          <Route path="/notifications-center" element={<PrivateRoute><Navigate to="/notifications" replace /></PrivateRoute>} />
          <Route path="/create-condo" element={<CreateCondoRoute><CreateCondo /></CreateCondoRoute>} />
          <Route path="/user-management" element={<AdminRoute><UserManagement /></AdminRoute>} />
          <Route path="/module-config" element={<AdminRoute><ModuleConfigPage /></AdminRoute>} />
          {/* P1-B (2026-09-08): Condo identity + module toggles, AdminRoute-gated like /module-config. */}
          <Route path="/admin/condo" element={<AdminRoute><CondoAdminPanel /></AdminRoute>} />
          <Route path="/admin/pricing" element={<SuperAdminRoute><PricingConfigPage /></SuperAdminRoute>} />
          <Route path="/admin/bulk-onboarding" element={<AdminRoute><BulkOnboardingPage /></AdminRoute>} />
          {/* (2026-09-07) Matriz de permisos en vivo: super-admin, datos en vivo del resolver. */}
          <Route path="/admin/entitlements-matrix" element={<SuperAdminRoute><EntitlementsMatrixPage /></SuperAdminRoute>} />
          <Route path="/admin/permissions" element={<AdminRoute><PermissionManager /></AdminRoute>} />
          <Route path="/empleados" element={<PrivateRoute><Dashboard><Employees /></Dashboard></PrivateRoute>} />

          {/* New Modules */}
          <Route path="/verify" element={<SecurityRoute><VerifyQR /></SecurityRoute>} />
          {/* (2026-09-24, bug #105) Guard-scoped walk-in "Sin QR" registry UI —
              the APIs existed but no /security/walk-ins page was routed. */}
          <Route path="/security/walk-ins" element={<SecurityRoute><SecurityWalkIns /></SecurityRoute>} />
          {/* 2026-09-09: condo-wide QR scan audit (adminOnly, BackendDev GET /qr-access/audit). */}
          <Route path="/building/admin/qr-audit" element={<AdminRoute><AppShell><LensDashboard /></AppShell></AdminRoute>} />
          {/* 2026-09-09: legacy path redirect. */}
          <Route path="/admin/qr-audit" element={<Navigate to="/building/admin/qr-audit" replace />} />
          <Route path="/supplier-profile" element={<PrivateRoute><SupplierProfile /></PrivateRoute>} />

          {/* Super Admin Only - Payment Config */}
          <Route path="/openpay-config" element={<AdminRoute><OpenpayConfig /></AdminRoute>} />

          {/* Mi ResidenciApp 2018 Modules */}
          <Route path="/quejas" element={<PrivateRoute><QuejasYSugerencias /></PrivateRoute>} />
          <Route path="/emergencias" element={<PrivateRoute><ProtectedRoute moduleId="emergencias"><NumerosEmergencia /></ProtectedRoute></PrivateRoute>} />
          <Route path="/numeros-emergencia" element={<PrivateRoute><Navigate to="/building/emergency" replace /></PrivateRoute>} />
          <Route path="/reglamentos" element={<PrivateRoute><Reglamentos /></PrivateRoute>} />
          <Route path="/caja-chica" element={<PrivateRoute><CajaChica /></PrivateRoute>} />
          <Route path="/casetas" element={<PrivateRoute><CasetasVigilancia /></PrivateRoute>} />
          <Route path="/casetas-vigilancia" element={<PrivateRoute><CasetasVigilancia /></PrivateRoute>} />
          <Route path="/empresas-seguridad" element={<PrivateRoute><EmpresasSeguridad /></PrivateRoute>} />
          <Route path="/personal-seguridad" element={<PrivateRoute><PersonalSeguridad /></PrivateRoute>} />
          <Route path="/reporte-visitas" element={<PrivateRoute><ReporteVisitas /></PrivateRoute>} />
          <Route path="/info-condominio" element={<PrivateRoute><InformacionCondominio /></PrivateRoute>} />
          <Route path="/perfil-completo" element={<PrivateRoute><Navigate to="/home/edicion-perfil" replace /></PrivateRoute>} />
          <Route path="/edicion-perfil" element={<PrivateRoute><EdicionPerfil /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Navigate to="/home/edicion-perfil" replace /></PrivateRoute>} />
          <Route path="/profile/security" element={<PrivateRoute><Navigate to="/home/edicion-perfil" replace /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><Navigate to="/home/edicion-perfil" replace /></PrivateRoute>} />
          <Route path="/llave-digital" element={<PrivateRoute><LlaveDigital /></PrivateRoute>} />
          <Route path="/qr" element={<PrivateRoute><QrScannerPage /></PrivateRoute>} />
          <Route path="/votaciones-avanzadas" element={<PrivateRoute><VotacionesAvanzadas /></PrivateRoute>} />
          <Route path="/categorias" element={<PrivateRoute><Categorias /></PrivateRoute>} />
          <Route path="/reserva-pago" element={<PrivateRoute><ReservaPago /></PrivateRoute>} />

          {/* Free Tier - Access & Emergency Modules */}
          <Route path="/my-qr" element={<PrivateRoute><Navigate to="/home/my-qr" replace /></PrivateRoute>} />
          <Route path="/resident-qr" element={<PrivateRoute><Navigate to="/building/resident-qr" replace /></PrivateRoute>} />
            {/* (Jaime 2026-08-31) Panic now mounts at /home/panic (inside AppShell/LensDashboard); legacy bookmarked routes redirect. */}
          <Route path="/panic" element={<PrivateRoute><Navigate to="/home/panic" replace /></PrivateRoute>} />
          <Route path="/panic-button" element={<PrivateRoute><Navigate to="/home/panic" replace /></PrivateRoute>} />
          <Route path="/travel" element={<PrivateRoute><TravelNotices /></PrivateRoute>} />
          <Route path="/travel-notices" element={<PrivateRoute><Navigate to="/home/travel" replace /></PrivateRoute>} />
          <Route path="/billing-portal" element={<PrivateRoute><Navigate to="/billing" replace /></PrivateRoute>} />
          <Route path="/directory" element={<PrivateRoute><Navigate to="/services/suppliers" replace /></PrivateRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        {/* Global plan-limit upgrade modal (opened from api.ts interceptor) */}
        <PlanLimitModal />
        <WalkInModal />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
